import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { saas, errMsg } from "@/lib/saasApi";

export default function AdminDiagnostics() {
  const [d, setD] = useState(null);
  const [stripe, setStripe] = useState(null);
  useEffect(() => {
    saas.adminDiagnostics().then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e)));
    saas.adminStripeEvents().then(({ data }) => setStripe(data)).catch(() => {});
  }, []);

  if (!d) return <p className="text-sm text-re-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="admin-diagnostics">
      <h1 className="font-heading text-2xl font-bold text-re-charcoal">Diagnostics</h1>
      <p className="text-sm text-re-muted">Owner-only technical data. This information is <strong>never</strong> shown to customers.</p>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-re-line bg-white p-5" data-testid="diag-key">
          <p className="text-sm text-re-muted">Rendy API key</p>
          <p className="mt-1 flex items-center gap-1.5 font-medium text-re-charcoal">
            {d.rendyKeyConfigured ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Configured</> : <><XCircle className="h-4 w-4 text-re-red" /> Missing</>}
          </p>
        </div>
        <div className="rounded-2xl border border-re-line bg-white p-5" data-testid="diag-pool">
          <p className="text-sm text-re-muted">Estimated Rendy balance</p>
          <p className="mt-1 font-heading text-2xl font-bold text-re-charcoal">{d.providerPool?.balance ?? 0}</p>
          <p className="text-xs text-re-muted">Last reconciliation: {d.providerPool?.syncedAt ? new Date(d.providerPool.syncedAt).toLocaleString("en-GB") : "never"}</p>
        </div>
        <div className="rounded-2xl border border-re-line bg-white p-5" data-testid="diag-stripe-webhook">
          <p className="text-sm text-re-muted">Stripe webhook secret</p>
          <p className="mt-1 flex items-center gap-1.5 font-medium text-re-charcoal">
            {stripe?.webhookConfigured ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Configured</> : <><XCircle className="h-4 w-4 text-re-red" /> Missing</>}
          </p>
        </div>
      </div>

      {/* Owner-only compliance note (never shown to customers) */}
      <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="peppol-note">
        <p className="text-sm font-semibold text-re-charcoal">Internal compliance note</p>
        <p className="mt-1 text-sm text-re-muted">Belgian B2B structured e-invoicing / Peppol integration required before production launch. Stripe PDF invoices alone do not satisfy Belgian structured B2B e-invoicing requirements.</p>
        <p className="mt-2 text-sm text-re-muted" data-testid="dpa-internal-note">Before scaling production processing of customer personal data, retain appropriate Article 28 / DPA documentation with material AI/video-processing subprocessors.</p>
      </div>

      {/* Stripe webhook events */}
      <div>
        <h2 className="mb-2 font-heading text-lg font-semibold text-re-charcoal">Stripe webhook events</h2>
        <div className="overflow-x-auto rounded-2xl border border-re-line bg-white" data-testid="diag-stripe-events">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Event ID</TableHead><TableHead>Type</TableHead><TableHead>Organisation</TableHead>
              <TableHead>Status</TableHead><TableHead>Error</TableHead><TableHead>Received</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(stripe?.events || []).map((e) => (
                <TableRow key={e.stripeEventId} data-testid={`stripe-event-${e.stripeEventId}`}>
                  <TableCell className="font-mono text-xs text-re-muted">{e.stripeEventId}</TableCell>
                  <TableCell className="text-re-charcoal">{e.eventType}</TableCell>
                  <TableCell className="text-re-muted">{e.organisationName || e.organisationId || "—"}</TableCell>
                  <TableCell><Badge variant={e.processingStatus === "processed" ? "secondary" : e.processingStatus === "error" ? "destructive" : "outline"}>{e.processingStatus}</Badge></TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs text-re-red" title={e.error || ""}>{e.error || "—"}</TableCell>
                  <TableCell className="text-xs text-re-muted">{e.receivedAt ? new Date(e.receivedAt).toLocaleString("en-GB") : "—"}</TableCell>
                </TableRow>
              ))}
              {(!stripe || stripe.events.length === 0) && <TableRow><TableCell colSpan={6} className="text-center text-re-muted">No Stripe events received yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-re-line bg-white" data-testid="diag-projects">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Project</TableHead><TableHead>Listing ID</TableHead><TableHead>Provider status</TableHead>
            <TableHead>Progress</TableHead><TableHead>Internal status</TableHead><TableHead>Error</TableHead><TableHead>Updated</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {d.projects.map((p) => (
              <TableRow key={p.id} data-testid={`diag-row-${p.id}`}>
                <TableCell className="font-medium text-re-charcoal">{p.projectName}</TableCell>
                <TableCell className="font-mono text-xs text-re-muted">{p.providerListingId || "—"}</TableCell>
                <TableCell><Badge variant="secondary">{p.providerStatus || "—"}</Badge></TableCell>
                <TableCell className="text-re-muted">{p.providerProgress ?? "—"}</TableCell>
                <TableCell className="text-re-muted">{p.status}</TableCell>
                <TableCell className="max-w-[200px] truncate text-xs text-re-red" title={p.internalError || ""}>{p.internalError || "—"}</TableCell>
                <TableCell className="text-xs text-re-muted">{p.updatedAt ? new Date(p.updatedAt).toLocaleString("en-GB") : "—"}</TableCell>
              </TableRow>
            ))}
            {d.projects.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-re-muted">No projects yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
